import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { BarChart3, Edit, Info } from "lucide-react";
import { Progress } from "../ui/progress";
import MaturityDefinitionsDialog from "./MaturityDefinitionsDialog";
const ConfidenceBadge = ({ confidence }) => {
    const normalized = (confidence ?? "medium").toLowerCase();
    if (normalized === "high")
        return <Badge className="bg-green-600 hover:bg-green-700">High</Badge>;
    if (normalized === "medium")
        return <Badge className="bg-yellow-600 hover:bg-yellow-700">Medium</Badge>;
    return <Badge variant="destructive">Low</Badge>;
};
const MaturityAssessment = ({ data }) => {
    const processes = data.process_maturity_scores ?? [];
    const [showDefinitions, setShowDefinitions] = useState(false);
    const uniqueId = useMemo(() => {
        if (data.unique_id)
            return data.unique_id;
        if (typeof window !== "undefined") {
            return localStorage.getItem("dashboard_unique_id") ?? undefined;
        }
        return undefined;
    }, [data.unique_id]);
    return (<>
      <div className="space-y-6">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-primary"/>
              </div>
              <div>
                <CardTitle>Maturity and Readiness Assessment</CardTitle>
                <CardDescription>
                  Evaluation of technical capabilities, data readiness, and organizational preparedness for AI implementation
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Process-Level Maturity Scores</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setShowDefinitions(true)}>
                <Edit className="h-4 w-4 mr-2"/>
                Edit Definitions
              </Button>
            </div>
            <CardDescription>
              Assessment across Technical, Data, and Organizational dimensions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {processes.length === 0 ? (<p className="text-sm text-muted-foreground">
                Upload a transcript and generate the dashboard to view process-level maturity scores.
              </p>) : (<div className="space-y-6">
                {processes.map((process) => (<div key={process.process} className="space-y-3 p-4 border rounded-lg bg-card">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="font-semibold text-foreground">{process.process}</h4>
                      <ConfidenceBadge confidence={process.confidence}/>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Technical Readiness</span>
                          <span className="font-medium">{process.technical_score}%</span>
                        </div>
                        <Progress value={process.technical_score} className="h-2"/>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Data Readiness</span>
                          <span className="font-medium">{process.data_score}%</span>
                        </div>
                        <Progress value={process.data_score} className="h-2"/>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Organizational Readiness</span>
                          <span className="font-medium">{process.organizational_score}%</span>
                        </div>
                        <Progress value={process.organizational_score} className="h-2"/>
                      </div>
                    </div>
                  </div>))}
              </div>)}
          </CardContent>
        </Card>

        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="h-4 w-4"/>
              Maturity Evaluation Methodology
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Technical Readiness:</p>
              <p>Evaluates existing automation, system integration capabilities, and technical infrastructure</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Data Readiness:</p>
              <p>Assesses data quality, availability, standardization, and accessibility for AI/ML models</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Organizational Readiness:</p>
              <p>Measures change management capabilities, skill levels, and cultural alignment for AI adoption</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <MaturityDefinitionsDialog open={showDefinitions} onOpenChange={setShowDefinitions} uniqueId={uniqueId}/>
    </>);
};
export default MaturityAssessment;
